import { useState, useMemo, useCallback, type RefObject } from "react"
import { Link } from "react-router-dom"
import { toast } from "react-toastify"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  type CustomMetricValueEntryDto,
  useCompanyCustomMetricControllerUpsertValuesMutation,
} from "~/ui-rtk/api/companyCustomMetricApi"
import {
  DEFAULT_REPORT_ID,
  filterWholesaleMetrics,
  isCustomGroup,
  resolveCatalogMetrics,
  sourcesForMetricKeys,
  type GroupVisualization,
  type KpiTableSlug,
  type ReportGroup,
} from "~/shared/kpi"
import { useSavedReportControllerUpdateReportMutation } from "~/ui-rtk/api/savedReportApi"
import useConnect, { type KpiConnectParams } from "./connect"
import {
  type KpiRow,
  insertTypedRowsAcrossTables,
} from "./utils/custom-metric-rows"
import {
  autoYearBackCompare,
  localDateFromStr,
  type ExplorerRange,
} from "./utils/explorer-compare"
import KPITable from "./components/KPITable"
import KPIGroupChart from "./components/KPIGroupChart"
import ExplorerView from "./components/ExplorerView"
import RegionalKPI from "./components/RegionalKPI"
import TableSortingModal from "./components/TableSortingModal"
import ReportEditorModal from "./components/ReportEditorModal"
import CustomMetricManagerModal from "./components/CustomMetricManagerModal"
import KPIExportSuccessDialog from "./components/KPIExportSuccessDialog"
import { useTableSettings } from "./hooks/useTableSettings"
import { useSynchronizedScroll } from "./hooks/useSynchronizedScroll"
import { useKPIExport } from "./hooks/useKPIExport"
import { useKpiReports } from "./hooks/useKpiReports"
import Select from "~/ui-rtk/components/ui/controls/Select"
import {
  DateRangePicker,
  GranularitySelector,
} from "~/ui-rtk/components/ui/controls"
import SegmentedToggle from "~/ui-rtk/components/ui/controls/SegmentedToggle/SegmentedToggle"
import { useURLSearchParams } from "~/ui-rtk/hooks/url"
import { DEFAULT_TABLE_SETTINGS } from "./constants/settings"
import Breadcrumbs from "~/ui-rtk/components/layout/Breadcrumbs"
import { useCheckServiceIsConnected } from "~/ui-rtk/hooks/connected-services"
import { CONNECTORS_IDS } from "~/ui-rtk/constants/sources"
import { brandRoutes } from "~/ui-rtk/constants/routes/brand-routes"
import { VISUALIZATIONS_MAP, AggregationType } from "~/ui-rtk/constants/charts"
import { useConnectedWholesaleServices } from "~/ui-rtk/hooks/wholesale-access"
import { useCountryBreakoutEnabled } from "~/ui-rtk/hooks/country-breakout-access"
import AmazonToggle from "~/ui-rtk/components/ui/controls/AmazonToggle/AmazonToggle"

const MARGINAL_LABEL_MAP: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "DoD Marginal",
  [AggregationType.WEEKLY]: "WoW Marginal",
  [AggregationType.MONTHLY]: "MoM Marginal",
}

const MARGINAL_SPEND_LABEL_MAP: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "DoD Marginal Spend",
  [AggregationType.WEEKLY]: "WoW Marginal Spend",
  [AggregationType.MONTHLY]: "MoM Marginal Spend",
}

// Period-over-period growth label, keyed to the selected granularity. Custom
// reports render an inline growth row per toggled metric using this label.
const GROWTH_LABEL_MAP: Record<AggregationType, string> = {
  [AggregationType.DAILY]: "DoD Growth",
  [AggregationType.WEEKLY]: "WoW Growth",
  [AggregationType.MONTHLY]: "MoM Growth",
}

// Per-group visualization switcher (custom groups only; builtin groups and
// the Default report chart via the convert-to-custom flow instead).
const VISUALIZATION_OPTIONS: Array<{
  label: string
  value: GroupVisualization
}> = [
  { label: "Table", value: "table" },
  { label: "Line", value: "line" },
  { label: "Bar", value: "bar" },
]

// Visualization overrides are keyed per REPORT, not just per group: group ids
// are only unique within a report (builtin-to-custom conversion keeps the
// deterministic slug id, e.g. "adSpend", so two reports that each converted
// the same builtin table share a group id). A bare group-id key would leak an
// override from one report into another.
// The same key also scopes a group chart's persisted height (sizeKey).
const vizOverrideKey = (reportId: string, groupId: string) =>
  `${reportId}:${groupId}`

// The page's views: the classic KPI tables, the unified Metrics Explorer
// (chart any catalog metric over an arbitrary range) and — for companies that
// opted into the country breakout — the Regional tables (USA / Canada).
// Persisted in the `view` URL param; the /home/metrics-explorer route opens on
// "explorer" via the initialView prop WITHOUT touching the URL.
type KpiView = "tables" | "explorer" | "regional"

const VIEW_URL_PARAM = "view"

const VIEW_OPTIONS: Array<{ label: string; value: KpiView }> = [
  { label: "Tables", value: "tables" },
  { label: "Explorer", value: "explorer" },
]

const REGIONAL_VIEW_OPTION: { label: string; value: KpiView } = {
  label: "Regional",
  value: "regional",
}

const isKpiView = (value: unknown): value is KpiView =>
  value === "tables" || value === "explorer" || value === "regional"

// Normalized shape the render loop consumes, regardless of whether a group is a
// builtin KPI slug (resolved from `tableConfigs`) or a config-driven custom
// group. Optional fields are read with `in` guards in the render loop, exactly
// as the legacy per-slug configs were.
type RenderableGroup = {
  data: Record<string, string | number | null | undefined>[]
  title: string
  showTotals: boolean
  scrollRef: RefObject<HTMLDivElement>
  tableKey: string
  available: boolean
  missingConnectors: { name: string }[]
  isLoading?: boolean
  wowMarginalLabel?: string
  marginalMetrics?: string[]
  /**
   * Custom groups only: typed custom-metric row keys the sum-all-rows totals
   * footer must skip because their values are already inside a folded
   * aggregate row of the same group (see typedMarginalExcludeKeys).
   */
  marginalExcludeKeys?: string[]
  wowGrowthMetricKeys?: string[]
  wowGrowthLabel?: string
  granularity?: AggregationType
}

export interface RevenueKPIProps {
  /**
   * View to open when the URL carries no `view` param — the
   * /home/metrics-explorer route passes "explorer" (prop-driven default, so
   * the route never rewrites the URL).
   */
  initialView?: KpiView
}

export default function RevenueKPI({
  initialView = "tables",
}: RevenueKPIProps = {}) {
  // Granularity state (daily, weekly or monthly)
  const [granularity, setGranularity] = useState<AggregationType>(
    AggregationType.WEEKLY,
  )

  // Active view, persisted in the URL (`view`); the prop only supplies the
  // default when the param is absent. The Regional view exists only for
  // companies opted into the country breakout, so a stale `view=regional`
  // (toggle switched off, shared link) falls back to the default.
  const isCountryBreakoutEnabled = useCountryBreakoutEnabled()
  const viewOptions = useMemo(
    () =>
      isCountryBreakoutEnabled
        ? [...VIEW_OPTIONS, REGIONAL_VIEW_OPTION]
        : VIEW_OPTIONS,
    [isCountryBreakoutEnabled],
  )
  const { params: urlParams, setParams: setUrlParams } = useURLSearchParams()
  const viewParam = urlParams[VIEW_URL_PARAM]
  const view: KpiView =
    isKpiView(viewParam) &&
    (viewParam !== "regional" || isCountryBreakoutEnabled)
      ? viewParam
      : initialView

  // The user-picked date / compare windows, shared by BOTH views (the Tables
  // header picker and the Explorer's picker bind to this one state, so a range
  // survives a view switch). It parameterizes the page's ONE useConnect call:
  // params flow only once a custom range was picked — otherwise undefined, so
  // the default fixed trailing window (and its already-loaded data) is used
  // byte-for-byte (MD-5282).
  const [pickedRange, setPickedRange] = useState<ExplorerRange | null>(null)
  const connectParams = useMemo<KpiConnectParams | undefined>(() => {
    if (!pickedRange) return undefined
    return {
      dateRange: pickedRange.dateRange,
      // ALWAYS explicit: a custom range with no picked compare derives its
      // same-length year-back window here rather than passing 'yoy' (whose
      // calendar-identity pairing collapses for ranges beyond 12 months).
      compareRange:
        pickedRange.compareRange ?? autoYearBackCompare(pickedRange.dateRange),
    }
  }, [pickedRange])

  // Check connector statuses first (moved up to use in useConnect)
  // Note: useCheckServiceIsConnected automatically checks all connectors in the group
  // (e.g., SHOPIFY checks both "shopify" and "shopify_airbyte")
  const isShopifyConnected = useCheckServiceIsConnected(CONNECTORS_IDS.SHOPIFY)

  // Use custom hooks for state management
  const {
    isLoading,
    brandValueIsLoading,
    mediaCampaignsIsLoading,
    initiativesIsLoading,
    financialsLoading,
    customMetricsLoading,
    customEditMeta,
    processedData,
    buildCustomGroupData,
    columnConfig,
    customEntries,
    initiativeEntries,
    catalogExtraEntries,
    typedRowInsertions,
    typedMarginalExcludeKeys,
    windows,
    depVar,
    amazonEnabled,
    toggleAmazon,
    isGaMultiPropertyEnabled,
  } = useConnect(granularity, isShopifyConnected, connectParams)
  const { tableOrder, expandedStates, setTableOrder, setExpandedStates } =
    useTableSettings()
  const {
    options: reportOptions,
    activeReportId,
    setActiveReportId,
    activeReport,
    activeConfig,
    isDefaultActive,
  } = useKpiReports()
  const {
    scrollRefs,
    getScrollRefByKey,
    handleSyncScroll,
    handleToggleExpandedWithSync,
  } = useSynchronizedScroll()
  const { exportAllTables, showSuccessDialog, handleCloseSuccessDialog } =
    useKPIExport({
      columnConfig,
      granularity,
    })
  // Check connector statuses using the useCheckServiceIsConnected hook
  // Note: Each check automatically includes both base and AIRBYTE versions from CONNECTORS_IDS_GROUPED
  const isAmazonPartnerConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.AMAZON_SELLING_PARTNER,
  )
  const isAmazonVendorConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.AMAZON_SELLING_PARTNER_VENDOR,
  )
  const isAmazonAdsConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.AMAZON_ADS,
  )
  const isGoogleAdsConnected = useCheckServiceIsConnected(
    CONNECTORS_IDS.GOOGLE_ADS,
  )
  const connectedWholesaleServices = useConnectedWholesaleServices()
  const isWholesaleConnected = connectedWholesaleServices.length > 0

  // Amazon is connected if any of its services are connected
  const isAmazonConnected =
    isAmazonPartnerConnected || isAmazonVendorConnected || isAmazonAdsConnected

  // Define table requirements and their connector dependencies
  const tableRequirements = {
    adSpend: {
      required: [], // No connector checks needed - always show
      optional: [],
    },
    mer: {
      required: [{ name: "Shopify", connected: isShopifyConnected }],
      optional: [],
    },
    croWebMerch: {
      required: [{ name: "Google Ads", connected: isGoogleAdsConnected }],
      optional: [],
    },
    searchKPIs: {
      required: [{ name: "Google Ads", connected: isGoogleAdsConnected }],
      optional: [],
    },
    metaAdsReachImpressions: {
      required: [], // No connector checks needed - always show
      optional: [],
    },
    metaAdsAudienceSegments: {
      required: [], // No connector checks needed - always show
      optional: [],
    },
    traffic: {
      required: [{ name: "Google Ads", connected: isGoogleAdsConnected }],
      optional: [],
    },
    averageOrderValues: {
      required: [{ name: "Shopify", connected: isShopifyConnected }],
      optional: [],
    },
    totalBusinessRevenue: {
      required: [
        { name: "Amazon", connected: isAmazonConnected },
        { name: "Shopify", connected: isShopifyConnected },
      ],
      optional: [],
    },
    brand: {
      required: [{ name: "Google Ads", connected: isGoogleAdsConnected }],
      optional: [],
    },
    gaRevenue: {
      required: [{ name: "Google Ads", connected: isGoogleAdsConnected }],
      optional: [],
    },
    shopifyRevenue: {
      required: [{ name: "Shopify", connected: isShopifyConnected }],
      optional: [],
    },
    shopifyCustomer: {
      required: [{ name: "Shopify", connected: isShopifyConnected }],
      optional: [],
    },
    shopifyEcommRevenue: {
      required: [{ name: "Shopify", connected: isShopifyConnected }],
      optional: [],
    },
    shopifyRetailRevenue: {
      required: [{ name: "Shopify", connected: isShopifyConnected }],
      optional: [],
    },
    amazonRevenue: {
      required: [{ name: "Amazon", connected: isAmazonConnected }],
      optional: [],
    },
    wholesale: {
      required: [], // Gated by isWholesaleConnected (any retailer connector); hidden, not prompted, when none
      optional: [],
    },
    shopifyContribution: {
      // The Profitability table spans Shopify AND Amazon margins — show it
      // when either side is connected (isTableAvailable is any-of).
      required: [
        { name: "Shopify", connected: isShopifyConnected },
        { name: "Amazon", connected: isAmazonConnected },
      ],
      optional: [],
    },
    revenueBreakdown: {
      required: [], // Always available as it's based on dep_var
      optional: [],
    },
    brandValue: {
      required: [], // Brand value should always be available
      optional: [],
    },
    brandValueBreakdown: {
      required: [], // Brand value breakdown should always be available
      optional: [],
    },
    digitalEngagement: {
      required: [], // No connector checks needed - always show
      optional: [],
    },
  }
  // Modal state
  const [isSortModalOpen, setIsSortModalOpen] = useState(false)
  const [isReportEditorOpen, setIsReportEditorOpen] = useState(false)
  const [isCustomMetricManagerOpen, setIsCustomMetricManagerOpen] =
    useState(false)
  // Set by a row's Upload-CSV button: the manager modal opens pre-scoped to
  // that metric (its CSV upload, directions, and template live there).
  const [customMetricManagerInitialId, setCustomMetricManagerInitialId] =
    useState<string | null>(null)

  // Inline cell edits from KPITable: one upsert per row-save; the tag
  // invalidation refetches the values query and the rows update in place.
  const [upsertCustomMetricValues] =
    useCompanyCustomMetricControllerUpsertValuesMutation()
  const handleSaveCustomMetric = useCallback(
    async (metricId: string, entries: CustomMetricValueEntryDto[]) => {
      try {
        await upsertCustomMetricValues({
          id: metricId,
          body: { values: entries },
        }).unwrap()
        toast.success(
          `Saved ${entries.length} value${entries.length === 1 ? "" : "s"}`,
        )
      } catch (error) {
        toast.error("Could not save values")
        throw error // keep the row in edit mode
      }
    },
    [upsertCustomMetricValues],
  )

  const handleOpenCustomMetricUpload = useCallback((metricId: string) => {
    setCustomMetricManagerInitialId(metricId)
    setIsCustomMetricManagerOpen(true)
  }, [])

  // Per-group visualization overrides (this session), keyed by
  // `${reportId}:${groupId}` (see vizOverrideKey): the switcher updates local
  // state immediately; the choice is additionally persisted onto the active
  // report's config when the user can update it (see
  // handleGroupVisualizationChange).
  const [vizOverrides, setVizOverrides] = useState<
    Record<string, GroupVisualization>
  >({})
  const [updateReport] = useSavedReportControllerUpdateReportMutation()

  // Global YoY Comparison state
  const [showYoYComparison, setShowYoYComparison] = useState(false)

  // Handler to update expanded state and sync scroll position
  const handleToggleExpanded = (tableKey: string, isExpanded: boolean) => {
    handleToggleExpandedWithSync(tableKey, isExpanded, (key, expanded) => {
      setExpandedStates(prev => ({
        ...prev,
        [key]: expanded,
      }))
    })
  }

  // Helper function to check if table should be shown (has at least one required connector)
  const isTableAvailable = (tableKey: string) => {
    const requirements =
      tableRequirements[tableKey as keyof typeof tableRequirements]
    if (!requirements) return false

    // If no connectors are required, table is always available
    if (requirements.required.length === 0) return true

    // Check if at least one required connector is connected
    return requirements.required.some(req => req.connected)
  }

  // Helper function to get missing connectors for a table
  const getMissingConnectors = (tableKey: string) => {
    const requirements =
      tableRequirements[tableKey as keyof typeof tableRequirements]
    if (!requirements) return []

    return requirements.required.filter(req => !req.connected)
  }

  // Table configurations with availability status
  const tableConfigs = useMemo(() => {
    const revenueKpiConfig = VISUALIZATIONS_MAP.revenue_kpi_dashboard_table
    const metricGroups = revenueKpiConfig?.props?.metricGroups || {}

    return {
      adSpend: {
        data: processedData.adSpendData,
        title: metricGroups.adSpend?.title || "Ad Spend",
        showTotals: true,
        scrollRef: scrollRefs.adSpendScrollRef,
        tableKey: "adSpend",
        available: isTableAvailable("adSpend"),
        missingConnectors: getMissingConnectors("adSpend"),
        wowMarginalLabel: MARGINAL_SPEND_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.total_ad_spend"], // Use total spend for marginal calculation
        granularity, // Pass granularity to determine date range behavior
      },
      mer: {
        data: processedData.merData,
        title: metricGroups.mer?.title || "Marketing Efficiency Ratio (MER)",
        showTotals: false,
        scrollRef: scrollRefs.merScrollRef,
        tableKey: "mer",
        available: isTableAvailable("mer"),
        missingConnectors: getMissingConnectors("mer"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      croWebMerch: {
        data: processedData.croWebMerchData,
        title: metricGroups.croWebMerch?.title || "CRO/Web Merch KPIs",
        showTotals: false,
        scrollRef: scrollRefs.croWebMerchScrollRef,
        tableKey: "croWebMerch",
        available: isTableAvailable("croWebMerch"),
        missingConnectors: getMissingConnectors("croWebMerch"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      searchKPIs: {
        data: processedData.searchKPIsData,
        title: metricGroups.searchKPIs?.title || "Search KPIs",
        showTotals: false,
        scrollRef: scrollRefs.searchKPIsScrollRef,
        tableKey: "searchKPIs",
        available: isTableAvailable("searchKPIs"),
        missingConnectors: getMissingConnectors("searchKPIs"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      metaAdsReachImpressions: {
        data: processedData.metaAdsReachImpressionsData,
        title:
          metricGroups.metaAdsReachImpressions?.title ||
          "Meta Ads Reach & Impressions",
        showTotals: false,
        scrollRef: scrollRefs.metaAdsReachImpressionsScrollRef,
        tableKey: "metaAdsReachImpressions",
        available: isTableAvailable("metaAdsReachImpressions"),
        missingConnectors: getMissingConnectors("metaAdsReachImpressions"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      metaAdsAudienceSegments: {
        data: processedData.metaAdsAudienceSegmentsData,
        title:
          metricGroups.metaAdsAudienceSegments?.title ||
          "Meta Ads Audience Segments",
        showTotals: false,
        scrollRef: scrollRefs.metaAdsAudienceSegmentsScrollRef,
        tableKey: "metaAdsAudienceSegments",
        available: isTableAvailable("metaAdsAudienceSegments"),
        missingConnectors: getMissingConnectors("metaAdsAudienceSegments"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      traffic: {
        data: processedData.trafficData,
        title: metricGroups.traffic?.title || "Traffic",
        showTotals: true,
        scrollRef: scrollRefs.trafficScrollRef,
        tableKey: "traffic",
        available: isTableAvailable("traffic"),
        missingConnectors: getMissingConnectors("traffic"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.ga_sessions"], // Use total sessions for marginal calculation
      },
      averageOrderValues: {
        data: processedData.averageOrderValuesData,
        title: metricGroups.averageOrderValues?.title || "Average Order Values",
        showTotals: false,
        scrollRef: scrollRefs.averageOrderValuesScrollRef,
        tableKey: "averageOrderValues",
        available: isTableAvailable("averageOrderValues"),
        missingConnectors: getMissingConnectors("averageOrderValues"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      totalBusinessRevenue: {
        data: processedData.totalBusinessRevenueData,
        title:
          metricGroups.totalBusinessRevenue?.title || "Total Business Revenue",
        showTotals: true,
        scrollRef: scrollRefs.totalBusinessRevenueScrollRef,
        tableKey: "totalBusinessRevenue",
        available: isTableAvailable("totalBusinessRevenue"),
        missingConnectors: getMissingConnectors("totalBusinessRevenue"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        // Always use Shopify for Total Business Revenue unless not connected
        marginalMetrics: isShopifyConnected
          ? ["metrics.total_business_revenue_shopify"]
          : ["metrics.total_business_revenue_ga"], // Use total business revenue for marginal calculation
      },
      brand: {
        data: processedData.brandData,
        title: metricGroups.brand?.title || "Brand Search KPIs",
        showTotals: false,
        scrollRef: scrollRefs.brandScrollRef,
        tableKey: "brand",
        available: isTableAvailable("brand"),
        missingConnectors: getMissingConnectors("brand"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      gaRevenue: {
        data: processedData.gaRevenueData,
        title: metricGroups.gaRevenue?.title || "GA Revenue by Traffic Source",
        showTotals: true,
        scrollRef: scrollRefs.gaRevenueScrollRef,
        tableKey: "gaRevenue",
        available: isTableAvailable("gaRevenue"),
        missingConnectors: getMissingConnectors("gaRevenue"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.ga_total_revenue"], // Use all metrics for marginal calculation
      },
      shopifyRevenue: {
        data: processedData.shopifyRevenueData,
        title:
          metricGroups.shopifyRevenue?.title ||
          "Shopify Web Revenue by Traffic Source",
        showTotals: true,
        scrollRef: scrollRefs.shopifyRevenueScrollRef,
        tableKey: "shopifyRevenue",
        available: isTableAvailable("shopifyRevenue"),
        missingConnectors: getMissingConnectors("shopifyRevenue"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.shopify_total_traffic_source_revenue"], // Use all metrics for marginal calculation
      },
      shopifyCustomer: {
        data: processedData.shopifyCustomerData,
        title: metricGroups.shopifyCustomer?.title || "Shopify - Total Revenue",
        showTotals: true,
        scrollRef: scrollRefs.shopifyCustomerScrollRef,
        tableKey: "shopifyCustomer",
        available: isTableAvailable("shopifyCustomer"),
        missingConnectors: getMissingConnectors("shopifyCustomer"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.shopify_total_customer_revenue"], // Use all metrics for marginal calculation
      },
      shopifyEcommRevenue: {
        data: processedData.shopifyEcommRevenueData,
        title:
          metricGroups.shopifyEcommRevenue?.title ||
          "Shopify - DTC Ecomm Revenue",
        showTotals: true,
        scrollRef: scrollRefs.shopifyEcommRevenueScrollRef,
        tableKey: "shopifyEcommRevenue",
        available: isTableAvailable("shopifyEcommRevenue"),
        missingConnectors: getMissingConnectors("shopifyEcommRevenue"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.shopify_ecomm_total_customer_revenue"],
      },
      shopifyRetailRevenue: {
        data: processedData.shopifyRetailRevenueData,
        title:
          metricGroups.shopifyRetailRevenue?.title ||
          "Shopify - DTC Retail Revenue",
        showTotals: true,
        scrollRef: scrollRefs.shopifyRetailRevenueScrollRef,
        tableKey: "shopifyRetailRevenue",
        available: isTableAvailable("shopifyRetailRevenue"),
        missingConnectors: getMissingConnectors("shopifyRetailRevenue"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.shopify_dtc_retail_total_customer_revenue"],
      },
      amazonRevenue: {
        data: processedData.amazonRevenueData,
        title: metricGroups.amazonRevenue?.title || "Amazon Revenue",
        showTotals: false,
        scrollRef: scrollRefs.amazonRevenueScrollRef,
        tableKey: "amazonRevenue",
        available: isTableAvailable("amazonRevenue"),
        missingConnectors: getMissingConnectors("amazonRevenue"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.amazon_total_revenue"], // Use all metrics for marginal calculation
      },
      wholesale: {
        data: processedData.wholesaleData,
        title: metricGroups.wholesale?.title || "3rd Party Retail Revenue",
        showTotals: false,
        scrollRef: scrollRefs.wholesaleScrollRef,
        tableKey: "wholesale",
        available: isWholesaleConnected,
        missingConnectors: [],
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics: ["metrics.wholesale_total_revenue"],
      },
      shopifyContribution: {
        data: processedData.shopifyContributionData,
        title: metricGroups.shopifyContribution?.title || "Profitability",
        showTotals: false,
        scrollRef: scrollRefs.shopifyContributionScrollRef,
        tableKey: "shopifyContribution",
        available: isTableAvailable("shopifyContribution"),
        missingConnectors: getMissingConnectors("shopifyContribution"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
      brandValue: {
        data: processedData.brandValueData,
        title: metricGroups.brandValue?.title || "Brand Value",
        showTotals: false,
        scrollRef: scrollRefs.brandValueScrollRef,
        tableKey: "brandValue",
        available: true, // Brand value should always be available
        missingConnectors: [],
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics:
          VISUALIZATIONS_MAP.revenue_kpi_dashboard_table?.props?.metricGroups
            ?.brandValue?.marginalMetrics || [],
      },
      brandValueBreakdown: {
        data: processedData.brandValueBreakdownData,
        title:
          metricGroups.brandValueBreakdown?.title || "Brand Value Breakdown",
        showTotals: false,
        scrollRef: scrollRefs.brandValueBreakdownScrollRef,
        tableKey: "brandValueBreakdown",
        available: isTableAvailable("brandValueBreakdown"),
        missingConnectors: getMissingConnectors("brandValueBreakdown"),
        isLoading: isLoading || brandValueIsLoading,
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
        marginalMetrics:
          VISUALIZATIONS_MAP.revenue_kpi_dashboard_table?.props?.metricGroups
            ?.brandValueBreakdown?.marginalMetrics || [],
      },
      digitalEngagement: {
        data: processedData.digitalEngagementData,
        title: metricGroups.digitalEngagement?.title || "Digital Engagement",
        showTotals: false,
        scrollRef: scrollRefs.digitalEngagementScrollRef,
        tableKey: "digitalEngagement",
        available: isTableAvailable("digitalEngagement"),
        missingConnectors: getMissingConnectors("digitalEngagement"),
      },
      revenueBreakdown: {
        data: processedData.revenueBreakdownData,
        title:
          metricGroups.revenueBreakdown?.title || "Baseline Revenue Breakdown",
        showTotals: false,
        scrollRef: scrollRefs.revenueBreakdownScrollRef,
        tableKey: "revenueBreakdown",
        available: isTableAvailable("revenueBreakdown"),
        missingConnectors: getMissingConnectors("revenueBreakdown"),
        wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
      },
    }
  }, [
    processedData,
    scrollRefs,
    isAmazonConnected,
    isShopifyConnected,
    isGoogleAdsConnected,
    isWholesaleConnected,
    granularity,
    depVar,
  ])

  // Ordered list of groups for the active report. The builtin Default report
  // (`activeConfig === null`) derives its order from the user's (localStorage)
  // table order mapped to builtin groups — identical to the legacy dashboard.
  // A selected custom report supplies its own ordered groups from its config.
  const activeGroups = useMemo<ReportGroup[]>(() => {
    if (activeConfig) {
      return activeConfig.groups
    }
    return tableOrder.map((slug: string) => ({
      kind: "builtin" as const,
      id: slug,
      slug: slug as KpiTableSlug,
    }))
  }, [activeConfig, tableOrder])

  // Resolve each group to the normalized render shape. Builtin groups reuse the
  // legacy per-slug `tableConfigs` entry verbatim (so the Default report renders
  // byte-for-byte as before); custom groups build their rows from the metric
  // catalog, split across cube sources by `buildCustomGroupData`.
  const resolvedRenderableGroups = useMemo<
    Array<{ group: ReportGroup; config: RenderableGroup | null }>
  >(
    () =>
      activeGroups.map(group => {
        if (isCustomGroup(group)) {
          const hasWowGrowth = (group.wowGrowthMetricKeys?.length ?? 0) > 0
          // A custom group can mix cube sources; show the loading state until
          // every source it actually references has resolved, so its rows
          // don't pop in a beat after the table renders. Calc keys expand to
          // their expressions' leaf sources (through nested calc refs), so a
          // formula over e.g. brand-value operands waits for that query too.
          const groupSources = sourcesForMetricKeys(
            group.metricKeys,
            activeConfig?.calculatedMetrics ?? [],
          )
          const config: RenderableGroup = {
            data: buildCustomGroupData(
              group.metricKeys,
              activeConfig?.calculatedMetrics ?? [],
            ),
            title: group.title,
            showTotals: group.showTotals ?? false,
            // Independent of growth: if the group opts into a totals row,
            // KPITable renders the aggregate marginal row — label it for the
            // selected granularity (distinct from the per-metric growth rows).
            wowMarginalLabel: MARGINAL_LABEL_MAP[granularity],
            scrollRef: getScrollRefByKey(group.id),
            tableKey: group.id,
            available: true,
            missingConnectors: [],
            isLoading:
              isLoading ||
              (groupSources.has("brand_value") && brandValueIsLoading) ||
              (groupSources.has("media_campaigns") &&
                mediaCampaignsIsLoading) ||
              (groupSources.has("initiatives") && initiativesIsLoading) ||
              (groupSources.has("computed") && financialsLoading) ||
              (groupSources.has("custom") && customMetricsLoading),
            ...(hasWowGrowth
              ? {
                  // Custom reports render an inline growth row beneath each
                  // toggled metric (see KPITable), rather than the legacy single
                  // summed marginal row used by the builtin dashboard tables.
                  wowGrowthMetricKeys: group.wowGrowthMetricKeys,
                  wowGrowthLabel: GROWTH_LABEL_MAP[granularity],
                }
              : {}),
          }
          return { group, config }
        }
        const builtin = tableConfigs[group.slug as keyof typeof tableConfigs]
        return {
          group,
          config: builtin ? (builtin as unknown as RenderableGroup) : null,
        }
      }),
    [
      activeGroups,
      activeConfig,
      tableConfigs,
      buildCustomGroupData,
      getScrollRefByKey,
      granularity,
      isLoading,
      brandValueIsLoading,
      mediaCampaignsIsLoading,
      initiativesIsLoading,
      financialsLoading,
      customMetricsLoading,
    ],
  )

  // Typed custom-metric display rows. Builtin groups: each row is placed into
  // exactly ONE rendered table (its dedicated table when available, else the
  // Total Business Revenue rollup) — this render layer is the only place that
  // knows table order and availability, so the cross-table dedup lives here.
  // Custom groups already inserted their rows in buildCustomGroupData; they
  // only get the totals-footer exclusion keys attached (a typed metric's row
  // must not be double-counted with the folded aggregate it's already inside).
  const renderableGroups = useMemo<
    Array<{ group: ReportGroup; config: RenderableGroup | null }>
  >(() => {
    if (typedRowInsertions.length === 0) return resolvedRenderableGroups

    const builtinEntries = resolvedRenderableGroups
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => !isCustomGroup(entry.group))
    const placedRows = insertTypedRowsAcrossTables(
      builtinEntries.map(({ entry }) => ({
        rows: (entry.config?.data ?? []) as KpiRow[],
        eligible: entry.config !== null && entry.config.available,
      })),
      typedRowInsertions,
    )
    const placedRowsByIndex = new Map(
      builtinEntries.map(({ index }, builtinIndex) => [
        index,
        placedRows[builtinIndex],
      ]),
    )

    return resolvedRenderableGroups.map((entry, index) => {
      const { group, config } = entry
      if (!config) return entry
      if (isCustomGroup(group)) {
        const excludeKeys = typedMarginalExcludeKeys(
          config.data.map(row => row.key),
        )
        return excludeKeys.length > 0
          ? { group, config: { ...config, marginalExcludeKeys: excludeKeys } }
          : entry
      }
      const rows = placedRowsByIndex.get(index)
      return rows === undefined || rows === config.data
        ? entry
        : { group, config: { ...config, data: rows } }
    })
  }, [resolvedRenderableGroups, typedRowInsertions, typedMarginalExcludeKeys])

  // Switch a custom group between table / line / bar. Local state flips
  // immediately; when the user can update the active report (same gate as
  // report saves: canUpdate = !isDefaultActive, with ownership enforced
  // server-side), the choice is also persisted onto the group's config —
  // fire-and-forget so a rejection (e.g. not the owner of a shared report)
  // still leaves the responsive local-only switch in place.
  const handleGroupVisualizationChange = useCallback(
    (groupId: string, visualization: GroupVisualization) => {
      // Merge the click into the overrides synchronously so both the state
      // update and the persistence payload below see it.
      const nextOverrides = {
        ...vizOverrides,
        [vizOverrideKey(activeReportId, groupId)]: visualization,
      }
      setVizOverrides(nextOverrides)
      if (isDefaultActive || !activeConfig) return
      // Build the payload from the cached config PLUS every session override,
      // not just the clicked group: the RTK cache can still be stale while a
      // prior switch's invalidation refetch is in flight, so a payload built
      // only from `activeConfig` would silently revert that earlier switch on
      // the server. Re-applying the full override map makes each PATCH carry
      // the session's complete intent (successive PATCHes are self-healing).
      const nextConfig = {
        version: activeConfig.version,
        groups: activeConfig.groups.map(group => {
          if (!isCustomGroup(group)) return group
          const groupVisualization =
            nextOverrides[vizOverrideKey(activeReportId, group.id)] ??
            group.visualization
          return groupVisualization !== undefined
            ? { ...group, visualization: groupVisualization }
            : group
        }),
        ...(activeConfig.calculatedMetrics
          ? { calculatedMetrics: activeConfig.calculatedMetrics }
          : {}),
      }
      try {
        updateReport({
          id: activeReportId,
          body: { config: nextConfig },
        })
          .unwrap()
          .catch(() => {
            // Persistence is best-effort; the local override already applied.
          })
      } catch {
        // Defensive: never let a persistence failure break the switch.
      }
    },
    [vizOverrides, isDefaultActive, activeConfig, activeReportId, updateReport],
  )

  // Resolve a builtin group's current metrics + totals so the report editor can
  // convert it into an editable custom group. Uses the same dynamic-group flag
  // wiring the legacy chart handler used, so a converted group starts identical
  // to what the Default report renders today.
  const resolveBuiltinGroupSeed = useCallback(
    (slug: string): { metricKeys: string[]; showTotals: boolean } => {
      const metricGroups =
        VISUALIZATIONS_MAP.revenue_kpi_dashboard_table?.props?.metricGroups ||
        {}
      const metricConfig = metricGroups[slug]
      let metrics = metricConfig?.metrics || []
      if (metricConfig?.getMetrics) {
        if (slug === "gaRevenue") {
          metrics = metricConfig.getMetrics(isGaMultiPropertyEnabled)
        } else if (slug === "totalBusinessRevenue") {
          metrics = metricConfig.getMetrics(
            amazonEnabled,
            isShopifyConnected,
            isWholesaleConnected,
          )
        } else if (slug === "revenueBreakdown") {
          metrics = metricConfig.getMetrics(depVar, amazonEnabled)
        }
      }
      if (slug === "wholesale") {
        metrics = filterWholesaleMetrics(metrics, connectedWholesaleServices)
      }
      const config = tableConfigs[slug as keyof typeof tableConfigs]
      return {
        metricKeys: metrics.map((metric: { key: string }) => metric.key),
        showTotals: config?.showTotals ?? false,
      }
    },
    [
      isGaMultiPropertyEnabled,
      amazonEnabled,
      isShopifyConnected,
      isWholesaleConnected,
      depVar,
      connectedWholesaleServices,
      tableConfigs,
    ],
  )

  // Explorer inputs: the ACTIVE report's calc defs (agreed scoping — the
  // Explorer offers exactly the calculated metrics of the selected report)
  // and the per-source loading flags the custom-group gate uses.
  const activeCalcDefs = useMemo(
    () => activeConfig?.calculatedMetrics ?? [],
    [activeConfig],
  )
  const explorerSourceLoading = useMemo(
    () => ({
      base: isLoading,
      brand_value: brandValueIsLoading,
      media_campaigns: mediaCampaignsIsLoading,
      initiatives: initiativesIsLoading,
      computed: financialsLoading,
      custom: customMetricsLoading,
    }),
    [
      isLoading,
      brandValueIsLoading,
      mediaCampaignsIsLoading,
      initiativesIsLoading,
      financialsLoading,
      customMetricsLoading,
    ],
  )

  return (
    <>
      <div className="p-6 bg-system-blue-900 min-h-screen">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <Breadcrumbs
                items={[
                  {
                    slug: "/revenue",
                    name: "Revenue",
                  },
                  {
                    slug: "/revenue/kpi",
                    name: "Revenue KPI",
                  },
                ]}
              />
            </div>
            <div className="flex items-center space-x-3">
              <SegmentedToggle
                variant="joined"
                options={viewOptions}
                value={view}
                onChange={value => setUrlParams({ [VIEW_URL_PARAM]: value })}
              />
              {view !== "regional" && (
                // Saved reports configure the dashboard tables and the
                // Explorer's calc metrics; the Regional view has a fixed set.
                <Select
                  aria-label="Saved report"
                  value={activeReportId}
                  onChange={event => setActiveReportId(event.target.value)}
                  options={reportOptions.map(option => ({
                    value: option.id,
                    label: option.isPrivate
                      ? `${option.name} (Private)`
                      : option.name,
                  }))}
                  classes={{ select: "h-system-2xl min-h-0 w-56 text-sm" }}
                />
              )}
              <GranularitySelector
                value={granularity}
                onChange={setGranularity}
              />

              {view !== "explorer" && (
                // Shared by the Tables and Regional views; the Explorer renders
                // its own picker bound to the same state. Display falls back
                // to the pipeline's default trailing window for the current
                // granularity until a range is picked.
                <DateRangePicker
                  start={
                    pickedRange?.dateRange[0] ??
                    localDateFromStr(windows.currentRange[0])
                  }
                  end={
                    pickedRange?.dateRange[1] ??
                    localDateFromStr(windows.currentRange[1])
                  }
                  granularity={granularity}
                  comparisonEnabled
                  compareStart={pickedRange?.compareRange?.[0]}
                  compareEnd={pickedRange?.compareRange?.[1]}
                  onChange={({ start, end, compareStart, compareEnd }) =>
                    setPickedRange({
                      dateRange: [start, end],
                      ...(compareStart && compareEnd
                        ? { compareRange: [compareStart, compareEnd] }
                        : {}),
                    })
                  }
                />
              )}

              <button
                onClick={() => setShowYoYComparison(!showYoYComparison)}
                className={cn(
                  "flex items-center space-x-2 px-system-lg py-system-sm h-system-2xl rounded-system-md transition-all duration-200 border shadow-md hover:shadow-lg",
                  showYoYComparison
                    ? "bg-system-success-700 hover:bg-system-success-600 border-system-success-600 text-white"
                    : "bg-system-blue-700 hover:bg-system-blue-600 border-system-blue-600 text-white",
                )}
                title={
                  showYoYComparison
                    ? `Hide YoY comparison`
                    : `Show YoY comparison`
                }
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
                <span className="text-sm font-medium">
                  {showYoYComparison ? `Hide YoY` : `Show YoY`}
                </span>
              </button>

              {view !== "regional" && toggleAmazon && (
                <AmazonToggle
                  amazonEnabled={amazonEnabled}
                  toggleAmazon={toggleAmazon}
                />
              )}

              {view === "tables" && (
                <>
                  <button
                    onClick={() => setIsSortModalOpen(true)}
                    className="flex items-center space-x-2 px-system-lg py-system-sm h-system-2xl bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8h16M4 16h16"
                      />
                    </svg>
                    <span className="text-white text-sm font-medium">
                      Reorder Tables
                    </span>
                  </button>

                  <button
                    onClick={() => setIsReportEditorOpen(true)}
                    className="flex items-center space-x-2 px-system-lg py-system-sm h-system-2xl bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors"
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    <span className="text-white text-sm font-medium">
                      Edit Report
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      setCustomMetricManagerInitialId(null)
                      setIsCustomMetricManagerOpen(true)
                    }}
                    className="flex items-center space-x-2 px-system-lg py-system-sm h-system-2xl bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors"
                    title="Create and edit custom metrics"
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    <span className="text-white text-sm font-medium">
                      Custom Metrics
                    </span>
                  </button>

                  <button
                    onClick={() => {
                      // Export the active report's rendered groups (works for both
                      // the Default dashboard and custom reports), in display order.
                      const visibleTables = renderableGroups
                        .map(({ group, config }) =>
                          config && config.available && config.data
                            ? {
                                key: group.id,
                                title: config.title,
                                data: config.data,
                              }
                            : null,
                        )
                        .filter(Boolean) as Array<{
                        key: string
                        title: string
                        data: Record<string, string | number | undefined>[]
                      }>
                      exportAllTables(visibleTables)
                    }}
                    className="flex items-center space-x-2 px-system-lg py-system-sm h-system-2xl bg-system-blue-700 hover:bg-system-blue-600 border border-system-blue-600 rounded-system-md transition-colors"
                    title="Export all visible tables"
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    <span className="text-white text-sm font-medium">
                      Export All
                    </span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {view === "explorer" && (
          <ExplorerView
            granularity={granularity}
            showYoY={showYoYComparison}
            windows={windows}
            range={pickedRange}
            onRangeChange={setPickedRange}
            calcDefs={activeCalcDefs}
            catalogExtraEntries={catalogExtraEntries}
            columnBuckets={customEditMeta.columnBuckets}
            buildCustomGroupData={buildCustomGroupData}
            sourceLoading={explorerSourceLoading}
            amazonEnabled={amazonEnabled}
            isGaMultiPropertyEnabled={isGaMultiPropertyEnabled}
          />
        )}

        {view === "regional" && (
          <RegionalKPI
            granularity={granularity}
            showYoYComparison={showYoYComparison}
            params={connectParams}
          />
        )}

        {view === "tables" &&
          renderableGroups.map(({ group, config }) => {
            if (!config) return null
            const tableKey = group.id

            // Feature-flagged-off tables (no connector to nudge the user toward)
            // render nothing. Tables with missing connectors fall through to the
            // setup prompt below.
            if (!config.available && config.missingConnectors.length === 0) {
              return null
            }

            // If table is not available, show setup prompt
            if (!config.available) {
              return (
                <div key={tableKey} className="mb-8">
                  <div className="bg-system-blue-800 border border-system-blue-700 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {config.title}
                        </h3>
                        <p className="text-system-blue-200 mb-4">
                          Connect the following services to view this table:
                        </p>
                        <div className="space-y-2">
                          {config.missingConnectors.map((connector, index) => (
                            <div
                              key={index}
                              className="flex items-center space-x-2"
                            >
                              <div className="w-2 h-2 bg-system-error-500 rounded-full"></div>
                              <span className="text-system-blue-200 text-sm">
                                {connector.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        <Link
                          to={brandRoutes.SETUP.CONNECTORS}
                          className="inline-flex items-center px-4 py-2 bg-system-success-600 hover:bg-system-success-700 text-white rounded-lg transition-colors font-medium"
                        >
                          <svg
                            className="w-4 h-4 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                            />
                          </svg>
                          Connect Services
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )
            }

            // Custom groups render per their configured visualization (with any
            // local override); builtin groups are always tables — they chart via
            // the report editor's convert-to-custom flow instead. Group data
            // stays built unconditionally either way (Export All and other
            // consumers iterate config.data even for chart-mode groups).
            const customGroup = isCustomGroup(group) ? group : null
            const effectiveVisualization: GroupVisualization = customGroup
              ? (vizOverrides[vizOverrideKey(activeReportId, customGroup.id)] ??
                customGroup.visualization ??
                "table")
              : "table"
            const visualizationSwitcher = customGroup ? (
              <SegmentedToggle
                variant="joined"
                options={VISUALIZATION_OPTIONS}
                value={effectiveVisualization}
                onChange={value =>
                  handleGroupVisualizationChange(
                    customGroup.id,
                    value as GroupVisualization,
                  )
                }
              />
            ) : undefined

            if (customGroup && effectiveVisualization !== "table") {
              return (
                <div key={tableKey} className="mb-8">
                  <KPIGroupChart
                    title={config.title}
                    rows={config.data}
                    columnBuckets={customEditMeta.columnBuckets}
                    metricDefs={resolveCatalogMetrics(
                      customGroup.metricKeys,
                      activeConfig?.calculatedMetrics ?? [],
                      catalogExtraEntries,
                    )}
                    sizeKey={vizOverrideKey(activeReportId, customGroup.id)}
                    visualization={effectiveVisualization}
                    granularity={granularity}
                    showYoY={showYoYComparison}
                    isLoading={
                      "isLoading" in config
                        ? (config.isLoading as boolean)
                        : isLoading
                    }
                    headerControls={visualizationSwitcher}
                  />
                </div>
              )
            }

            return (
              <div key={tableKey} className="mb-8">
                <KPITable
                  data={
                    config.data as {
                      key?: string
                      metric?: string
                      type?: string
                      suffix?: string
                      [key: string]: string | number | undefined
                    }[]
                  }
                  columns={columnConfig}
                  title={config.title}
                  showTotals={config.showTotals}
                  isExpanded={expandedStates[tableKey] ?? true}
                  scrollRef={config.scrollRef}
                  onScroll={scrollLeft =>
                    handleSyncScroll(scrollLeft, config.scrollRef)
                  }
                  onToggleExpanded={handleToggleExpanded}
                  tableKey={config.tableKey}
                  showYoYComparison={showYoYComparison}
                  isLoading={
                    "isLoading" in config
                      ? (config.isLoading as boolean)
                      : isLoading
                  }
                  wowMarginalLabel={
                    "wowMarginalLabel" in config
                      ? (config.wowMarginalLabel as string)
                      : undefined
                  }
                  marginalMetrics={
                    "marginalMetrics" in config
                      ? (config.marginalMetrics as string[])
                      : undefined
                  }
                  marginalExcludeKeys={config.marginalExcludeKeys}
                  wowGrowthMetricKeys={
                    "wowGrowthMetricKeys" in config
                      ? (config.wowGrowthMetricKeys as string[])
                      : undefined
                  }
                  wowGrowthLabel={
                    "wowGrowthLabel" in config
                      ? (config.wowGrowthLabel as string)
                      : undefined
                  }
                  headerControls={visualizationSwitcher}
                  customEditMeta={customEditMeta}
                  onSaveCustomMetric={handleSaveCustomMetric}
                  onOpenCustomMetricUpload={handleOpenCustomMetricUpload}
                />
              </div>
            )
          })}

        <TableSortingModal
          isOpen={isSortModalOpen}
          onClose={() => setIsSortModalOpen(false)}
          tableOrder={tableOrder}
          setTableOrder={setTableOrder}
          tableConfigs={tableConfigs}
          defaultTableOrder={DEFAULT_TABLE_SETTINGS.order}
        />

        <ReportEditorModal
          isOpen={isReportEditorOpen}
          onClose={() => setIsReportEditorOpen(false)}
          initialGroups={activeGroups}
          initialName={isDefaultActive ? "" : (activeReport?.name ?? "")}
          initialIsPrivate={activeReport?.isPrivate ?? false}
          initialCalculatedMetrics={activeConfig?.calculatedMetrics ?? []}
          customEntries={customEntries}
          initiativeEntries={initiativeEntries}
          canUpdate={!isDefaultActive}
          activeReportId={activeReportId}
          resolveBuiltinGroupSeed={resolveBuiltinGroupSeed}
          onSaved={setActiveReportId}
          onDeleted={() => setActiveReportId(DEFAULT_REPORT_ID)}
        />

        <CustomMetricManagerModal
          isOpen={isCustomMetricManagerOpen}
          initialMetricId={customMetricManagerInitialId}
          onClose={() => {
            setIsCustomMetricManagerOpen(false)
            setCustomMetricManagerInitialId(null)
          }}
        />

        <KPIExportSuccessDialog
          isOpen={showSuccessDialog}
          onClose={handleCloseSuccessDialog}
        />
      </div>
    </>
  )
}
